import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../api/useAxios";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Package,
  Calendar,
  CreditCard,
  DollarSign,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle
} from "lucide-react";

const CustomersView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const axiosInstance = useAxios();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCustomer = async () => {
    const res = await axiosInstance.get(`/users/${id}`);
    setCustomer(res.data.data);
    setStatus(res.data.data.active ?? true);
  };

  const fetchOrders = async () => {
    const res = await axiosInstance.get(`/orders/customer/${id}`);
    setOrders(res.data.data);
  };

  const handleStatusUpdate = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/users/${id}`, { active: status });
      // Optional: Show success toast here
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchCustomer(), fetchOrders()])
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-lg font-medium text-gray-700">Loading customer details...</p>
        <p className="text-sm text-gray-500 mt-2">Please wait a moment</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Customer Not Found</h2>
          <p className="text-gray-600 mb-6">The customer you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/customers")}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-6 group"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back</span>
        </button>

        <h1 className="text-3xl font-bold text-gray-900">Customer Profile</h1>
        <p className="text-gray-600 mt-2">View and manage customer information and orders</p>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Customer Details Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{customer.fullName}</h2>
              <div className="flex items-center gap-4 mt-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {status ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {status ? 'Active' : 'Inactive'}
                </span>
                <span className="text-sm text-gray-500">Customer ID: {id}</span>
              </div>
            </div>

            {/* Status Toggle */}
            <div className="mt-4 md:mt-0">
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Status</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <select
                    value={status ? "true" : "false"}
                    onChange={(e) => {
                      setStatus(e.target.value === "true");
                      handleStatusUpdate();
                    }}
                    disabled={isSaving}
                    className="appearance-none bg-white border border-gray-300 rounded-lg pl-10 pr-8 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 cursor-pointer min-w-[160px]"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    {status ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
              </div>
            </div>
          </div>

          {/* Customer Info Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-700">Full Name</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">{customer.fullName}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Phone className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-700">Mobile Number</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">{customer.mobileNumber}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Mail className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="font-medium text-gray-700">Email Address</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900 break-all">{customer.email}</p>
            </div>
          </div>
        </div>

        {/*Address */}
        <div className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <h2 className="text-2xl font-bold text-gray-900">Address</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2 justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-700 flex items-center gap-2">
                   Address
                </h3>
                </div>
                  <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                    Default
                  </span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{customer.address}</p>
              <div className="mt-2 text-sm text-gray-700 space-y-1">
                <p><span className="font-medium">Flat No:</span> {customer.flatNo ?? "N/A"}</p>
                <p><span className="font-medium">House No:</span> {customer.houseNo ?? "N/A"}</p>
                <p><span className="font-medium">Area:</span> {customer.area ?? "N/A"}</p>
                <p><span className="font-medium">Street:</span> {customer.street ?? "N/A"}</p>
                <p><span className="font-medium">Landmark:</span> {customer.landmark ?? "N/A"}</p>
              </div>

            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-700">Address</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">{customer.address}</p>
              <div className="mt-2 text-sm text-gray-700 space-y-1">
                <p><span className="font-medium">Flat No:</span> {customer.flatNo ?? "N/A"}</p>
                <p><span className="font-medium">House No:</span> {customer.houseNo ?? "N/A"}</p>
                <p><span className="font-medium">Area:</span> {customer.area ?? "N/A"}</p>
                <p><span className="font-medium">Street:</span> {customer.street ?? "N/A"}</p>
                <p><span className="font-medium">Landmark:</span> {customer.landmark ?? "N/A"}</p>
              </div>

            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 transition-transform duration-200 hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-700">Address</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900 break-all">{customer.address}</p>
              <div className="mt-2 text-sm text-gray-700 space-y-1">
                <p><span className="font-medium">Flat No:</span> {customer.flatNo ?? "N/A"}</p>
                <p><span className="font-medium">House No:</span> {customer.houseNo ?? "N/A"}</p>
                <p><span className="font-medium">Area:</span> {customer.area ?? "N/A"}</p>
                <p><span className="font-medium">Street:</span> {customer.street ?? "N/A"}</p>
                <p><span className="font-medium">Landmark:</span> {customer.landmark ?? "N/A"}</p>
              </div>

            </div>
          </div>
        </div>

        {/* Orders Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Customer Orders</h2>
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <Package className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-gray-700">{orders.length}</span>
              <span className="text-gray-600">Orders</span>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 max-w-md mx-auto">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Yet</h3>
                <p className="text-gray-500">This customer hasn't placed any orders.</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Order No
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Date
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Payment
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Amount
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr
                      key={order.orderId}
                      className="hover:bg-blue-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate(`/orders/${order.orderId}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900">{order.orderNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(order.orderedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(order.orderedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                          ${order.orderStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            order.orderStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              order.orderStatus === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'}`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                          ${order.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' :
                            order.paymentStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'}`}>
                          {order.paymentStatus.replaceAll("_", " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-gray-900">₹{order.finalAmount}</span>
                          {order.paymentStatus === 'PAID' && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomersView;